import type { LucideIcon } from 'lucide-react';

/** Destino da navegação. `permission` esconde o item de quem não tem acesso (conveniência). */
export interface NavLeaf {
  label: string;
  to: string;
  permission?: string;
}

/**
 * Módulo da barra lateral. Três formas: link (`to`), grupo que abre (`children`) ou módulo
 * ainda não construído (`soon`), que aparece para mostrar a direção do produto, mas não navega.
 */
export type NavItem =
  | (NavLeaf & { icon: LucideIcon; kind: 'link' })
  | { kind: 'group'; label: string; icon: LucideIcon; children: NavLeaf[] }
  | { kind: 'soon'; label: string; icon: LucideIcon; description: string };

/** Item navegável achatado — para a busca e a trilha de navegação. */
export interface NavTarget extends NavLeaf {
  parent?: string;
}

export function visibleNav(items: NavItem[], can: (permission: string) => boolean): NavItem[] {
  const allowed = (leaf: NavLeaf) => !leaf.permission || can(leaf.permission);
  return items.flatMap((item): NavItem[] => {
    if (item.kind === 'link') return allowed(item) ? [item] : [];
    if (item.kind === 'group') {
      const children = item.children.filter(allowed);
      return children.length > 0 ? [{ ...item, children }] : [];
    }
    return [item];
  });
}

export function navTargets(items: NavItem[]): NavTarget[] {
  return items.flatMap((item): NavTarget[] => {
    if (item.kind === 'link') return [{ label: item.label, to: item.to }];
    if (item.kind === 'group')
      return item.children.map((leaf) => ({ ...leaf, parent: item.label }));
    return [];
  });
}

/**
 * Destino ao qual a rota atual pertence: o de prefixo mais longo, para que `/produtos/123`
 * acenda "Catálogo". A raiz só vale na correspondência exata — senão acenderia em tudo.
 */
export function matchTarget(targets: NavTarget[], pathname: string): NavTarget | undefined {
  return targets
    .filter((target) =>
      target.to === '/'
        ? pathname === '/'
        : pathname === target.to || pathname.startsWith(`${target.to}/`),
    )
    .sort((a, b) => b.to.length - a.to.length)[0];
}
